import {
  IconColor,
  SalvButton,
  SalvModal,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  Size,
} from '@salvapp/ui-components';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { InternalApiExportFileType } from '../api-internal';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { useDispatch } from 'react-redux';
import { downloadDocument } from '../common/document/helper';

interface Props {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  exportEntities: (fileType: InternalApiExportFileType) => Promise<File | void>;
  synchronous?: boolean;
}

interface File {
  headers: Headers;
  body: Blob;
}

const ExportModal = ({ show, setShow, exportEntities, synchronous = false }: Props) => {
  const dispatch = useDispatch();
  const [selectedFileType, setSelectedFileType] = useState<InternalApiExportFileType>(InternalApiExportFileType.CSV);

  const downloadSync = async () => {
    const document = (await exportEntities(selectedFileType)) as File;
    const filename = document.headers.get('Content-Disposition')?.split('filename=')[1];

    setShow(false);
    downloadDocument(document.body, filename ? filename : 'export');
  };

  const downloadAsync = async () => {
    await exportEntities(selectedFileType);
    setShow(false);
    dispatch(
      showConfirmationModal({
        title: 'Export is in progress',
        description: (
          <div>
            <div className={'m-b-8'}>Your export file is now being processed!</div>
            <div>A download link will be sent directly to your email once the export is ready.</div>
          </div>
        ),
        onConfirm: null,
        cancelText: 'Close',
        icon: faCircleCheck,
        iconColor: IconColor.SUCCESS,
      })
    );
  };

  return (
    <SalvModal show={show} onHide={() => setShow(false)}>
      <SalvModal.Header closeButton>Export</SalvModal.Header>
      <SalvModal.Body>
        <SalvRadioToggleGroup
          label={'File format of the exported file'}
          onChange={(value) => setSelectedFileType(value as InternalApiExportFileType)}
          value={selectedFileType}
        >
          <SalvRadioToggleButton value={InternalApiExportFileType.CSV} label={'.csv'} />
          <SalvRadioToggleButton value={InternalApiExportFileType.XLSX} label={'.xlsx'} />
        </SalvRadioToggleGroup>
      </SalvModal.Body>
      <SalvModal.Footer>
        <SalvButton label='Cancel' variant='muted' onClick={() => setShow(false)} size={Size.MEDIUM} />
        <SalvButton
          label='Export'
          disabled={!selectedFileType}
          onClick={async () => {
            if (synchronous) {
              await downloadSync();
            } else {
              await downloadAsync();
            }
          }}
          variant='primary'
          size={Size.MEDIUM}
        />
      </SalvModal.Footer>
    </SalvModal>
  );
};

export default ExportModal;
